import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Users, Filter, Search, AlertCircle } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useUserStore } from '../../lib/userStore';
import { useThemeStore } from '../../lib/themeStore';
import { getWatchlistPlayers, removeFromWatchlist } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface WatchlistPlayer {
  id: number;
  player_id: number;
  player_name: string;
  player_position: string;
  is_goalie: boolean;
  salary?: number;
  team_abbrev?: string;
  created_at: string;
  owner_name: string | null;
  is_free_agent: boolean;
}

type SortField = 'player_name' | 'player_position' | 'salary' | 'team_abbrev' | 'created_at' | 'owner_name';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export function WatchlistPage() {
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();

  const [watchlistPlayers, setWatchlistPlayers] = useState<WatchlistPlayer[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<WatchlistPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState<string>('ALL');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc',
  });

  useEffect(() => {
    if (!selectedLeague || !currentUserId) {
      navigate('/dashboard');
      return;
    }

    loadWatchlistPlayers();
  }, [selectedLeague, currentUserId, navigate]);

  useEffect(() => {
    filterAndSortPlayers();
  }, [watchlistPlayers, searchTerm, positionFilter, sortConfig]);

  const loadWatchlistPlayers = async () => {
    if (!selectedLeague || !currentUserId) return;

    try {
      setLoading(true);
      const result = await getWatchlistPlayers(selectedLeague.id, Number(currentUserId));
      
      if (result.data && !result.error) {
        setWatchlistPlayers(result.data);
      } else {
        console.error('Error loading watchlist:', result.error);
        toast.error(
          language === 'fr' 
            ? 'Erreur lors du chargement de la liste de surveillance' 
            : 'Error loading watchlist'
        );
      }
    } catch (error) {
      console.error('Error loading watchlist:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors du chargement de la liste de surveillance'
          : 'Error loading watchlist'
      );
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPlayers = () => {
    let filtered = [...watchlistPlayers];

    // Filtrage par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(player => 
        player.player_name.toLowerCase().includes(term) ||
        player.team_abbrev?.toLowerCase().includes(term)
      );
    }

    // Filtrage par position
    if (positionFilter !== 'ALL') {
      if (positionFilter === 'G') {
        filtered = filtered.filter(player => player.is_goalie);
      } else {
        filtered = filtered.filter(player => 
          !player.is_goalie && player.player_position === positionFilter
        );
      }
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.field];
      let bValue: any = b[sortConfig.field];

      // Gestion des valeurs nulles
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Conversion pour le tri
      if (sortConfig.field === 'salary') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (sortConfig.field === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredPlayers(filtered);
  };

  const handleSort = (field: SortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleRemoveFromWatchlist = async (playerId: number, playerName: string) => {
    if (!selectedLeague || !currentUserId || actionInProgress) return;

    try {
      setActionInProgress(true);
      const result = await removeFromWatchlist(selectedLeague.id, Number(currentUserId), playerId);
      
      if (result.success) {
        setWatchlistPlayers(prev => prev.filter(p => p.player_id !== playerId));
        toast.success(
          language === 'fr' 
            ? `${playerName} retiré de la liste de surveillance` 
            : `${playerName} removed from watchlist`
        );
      } else {
        throw new Error(result.error?.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la suppression de la liste de surveillance'
          : 'Error removing from watchlist'
      );
    } finally {
      setActionInProgress(false);
    }
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getPlayerImageUrl = (player: WatchlistPlayer) => {
    if (imageErrors[player.player_id]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${player.team_abbrev}/${player.player_id}.png`;
  };

  const formatSalary = (salary?: number) => {
    if (!salary) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(salary);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      language === 'fr' ? 'fr-FR' : 'en-US',
      { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  };

  const uniquePositions = Array.from(
    new Set(watchlistPlayers.map(p => p.is_goalie ? 'G' : p.player_position))
  ).sort();

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              {language === 'fr' ? 'Chargement...' : 'Loading...'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex align-items-center mb-3">
            <button
              className="btn btn-outline-secondary me-3 d-flex align-items-center"
              onClick={() => navigate('/my-office')}
            >
              <ArrowLeft size={20} className="me-2" />
              {language === 'fr' ? 'Retour' : 'Back'}
            </button>
            <h1 className="h3 mb-0 d-flex align-items-center">
              <Eye size={28} className="me-3 text-primary" />
              {language === 'fr' ? 'Joueurs Surveillés' : 'Watched Players'}
              <span className="badge bg-primary ms-3">{filteredPlayers.length}</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="row mb-4">
        <div className="col-lg-6 mb-3">
          <div className="input-group">
            <span className="input-group-text">
              <Search size={16} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder={language === 'fr' ? 'Rechercher un joueur...' : 'Search player...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-3 mb-3">
          <div className="input-group">
            <span className="input-group-text">
              <Filter size={16} />
            </span>
            <select
              className="form-select"
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
            >
              <option value="ALL">
                {language === 'fr' ? 'Toutes positions' : 'All positions'}
              </option>
              {uniquePositions.map(pos => (
                <option key={pos} value={pos}>
                  {pos === 'G' ? (language === 'fr' ? 'Gardien' : 'Goalie') : pos}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-lg-3 mb-3">
          <button
            className="btn btn-outline-primary w-100"
            onClick={loadWatchlistPlayers}
            disabled={loading}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" />
            ) : null}
            {language === 'fr' ? 'Actualiser' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Message si aucun joueur */}
      {filteredPlayers.length === 0 && !loading && (
        <div className="text-center py-5">
          <AlertCircle size={64} className="text-muted mb-3" />
          <h4 className="text-muted">
            {language === 'fr' ? 'Aucun joueur surveillé' : 'No watched players'}
          </h4>
          <p className="text-muted">
            {language === 'fr' 
              ? 'Ajoutez des joueurs à votre liste de surveillance depuis les pages agents libres, mon équipe ou intra-ligue.'
              : 'Add players to your watchlist from the free agents, my team, or other teams pages.'
            }
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/free-agents')}
          >
            {language === 'fr' ? 'Voir les agents libres' : 'View free agents'}
          </button>
        </div>
      )}

      {/* Tableau */}
      {filteredPlayers.length > 0 && (
        <div className="row">
          <div className="col">
            {/* Vue Desktop - Tableau */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table className="table table-hover table-dark">
                  <thead className="table-dark">
                    <tr>
                      <th>{language === 'fr' ? 'Photo' : 'Photo'}</th>
                      <th
                        onClick={() => handleSort('player_name')}
                        style={{ cursor: 'pointer' }}
                        className="user-select-none"
                      >
                        {language === 'fr' ? 'Nom' : 'Name'}
                        {sortConfig.field === 'player_name' && (
                          <span className="ms-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort('player_position')}
                        style={{ cursor: 'pointer' }}
                        className="user-select-none"
                      >
                        {language === 'fr' ? 'Position' : 'Position'}
                        {sortConfig.field === 'player_position' && (
                          <span className="ms-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort('team_abbrev')}
                        style={{ cursor: 'pointer' }}
                        className="user-select-none"
                      >
                        {language === 'fr' ? 'Équipe' : 'Team'}
                        {sortConfig.field === 'team_abbrev' && (
                          <span className="ms-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort('salary')}
                        style={{ cursor: 'pointer' }}
                        className="user-select-none"
                      >
                        {language === 'fr' ? 'Salaire' : 'Salary'}
                        {sortConfig.field === 'salary' && (
                          <span className="ms-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort('owner_name')}
                        style={{ cursor: 'pointer' }}
                        className="user-select-none"
                      >
                        {language === 'fr' ? 'Propriétaire' : 'Owner'}
                        {sortConfig.field === 'owner_name' && (
                          <span className="ms-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort('created_at')}
                        style={{ cursor: 'pointer' }}
                        className="user-select-none"
                      >
                        {language === 'fr' ? 'Ajouté le' : 'Added on'}
                        {sortConfig.field === 'created_at' && (
                          <span className="ms-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th>{language === 'fr' ? 'Actions' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlayers.map((player) => (
                      <tr key={player.id}>
                        <td>
                          <img 
                            src={getPlayerImageUrl(player)} 
                            alt={player.player_name}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            className="border"
                            onError={() => handleImageError(player.player_id)}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Users size={18} className="me-3 text-primary" />
                            <div>
                              <div className="fw-bold fs-6 text-white" style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                                {player.player_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge fs-6 px-3 py-2 ${player.is_goalie ? 'bg-info' : 'bg-secondary'}`}>
                            {player.is_goalie 
                              ? (language === 'fr' ? 'Gardien' : 'Goalie')
                              : player.player_position
                            }
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold fs-6 text-primary">{player.team_abbrev || '-'}</span>
                        </td>
                        <td className="fw-semibold fs-6">{formatSalary(player.salary)}</td>
                        <td>
                          {player.is_free_agent ? (
                            <span className="badge bg-success fs-6 px-3 py-2">
                              {language === 'fr' ? 'Agent libre' : 'Free Agent'}
                            </span>
                          ) : (
                            <span className="badge bg-warning text-dark fs-6 px-3 py-2">
                              {player.owner_name || 'Propriétaire inconnu'}
                            </span>
                          )}
                        </td>
                        <td className="text-muted">{formatDate(player.created_at)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => navigate(`/players/${player.player_id}`)}
                              title={language === 'fr' ? 'Voir les détails' : 'View details'}
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveFromWatchlist(player.player_id, player.player_name)}
                              disabled={actionInProgress}
                              title={language === 'fr' ? 'Retirer de la surveillance' : 'Remove from watchlist'}
                            >
                              {actionInProgress ? (
                                <span className="spinner-border spinner-border-sm" role="status" />
                              ) : (
                                <EyeOff size={14} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Vue Mobile - Cartes */}
            <div className="d-block d-md-none">
              {filteredPlayers.map((player) => (
                <div key={player.id} className="card mb-3" style={{ 
                  background: theme === 'dark' ? 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.04))' : 'var(--bs-white)',
                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.125)'
                }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-start mb-3">
                      <img 
                        src={getPlayerImageUrl(player)} 
                        alt={player.player_name}
                        style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
                        className="border me-3"
                        onError={() => handleImageError(player.player_id)}
                      />
                      <div className="flex-grow-1">
                        <h6 className={`mb-1 fw-bold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                          {player.player_name}
                        </h6>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className={`badge ${player.is_goalie ? 'bg-info' : 'bg-secondary'}`}>
                            {player.is_goalie 
                              ? (language === 'fr' ? 'Gardien' : 'Goalie')
                              : player.player_position
                            }
                          </span>
                          <span className="text-primary fw-bold">{player.team_abbrev || '-'}</span>
                        </div>
                        <div className={`small ${theme === 'dark' ? 'text-light' : 'text-muted'}`}>
                          <div><strong>{language === 'fr' ? 'Salaire' : 'Salary'}:</strong> {formatSalary(player.salary)}</div>
                          <div className="mb-2">
                            <strong>{language === 'fr' ? 'Propriétaire' : 'Owner'}:</strong>{' '}
                            {player.is_free_agent ? (
                              <span className="badge bg-success">
                                {language === 'fr' ? 'Agent libre' : 'Free Agent'}
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                {player.owner_name || 'Propriétaire inconnu'}
                              </span>
                            )}
                          </div>
                          <div><strong>{language === 'fr' ? 'Ajouté le' : 'Added on'}:</strong> {formatDate(player.created_at)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-2 justify-content-end">
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => navigate(`/players/${player.player_id}`)}
                        title={language === 'fr' ? 'Voir les détails' : 'View details'}
                      >
                        <Eye size={14} className="me-1" />
                        {language === 'fr' ? 'Détails' : 'Details'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveFromWatchlist(player.player_id, player.player_name)}
                        disabled={actionInProgress}
                        title={language === 'fr' ? 'Retirer de la surveillance' : 'Remove from watchlist'}
                      >
                        {actionInProgress ? (
                          <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          <>
                            <EyeOff size={14} className="me-1" />
                            {language === 'fr' ? 'Retirer' : 'Remove'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}